import * as React from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useStore, useTranslate } from 'react-admin';
import { Button } from '@mui/material';

import { getAccessToken, getIdentityCached } from '../api/tokenStorage';
import { ACTIVE_APP_STORE_KEY } from './activeAppStore';
import { DEFAULT_ACTIVE_APP_ID, workspaceRootPath } from './appsRegistry';
import { useTenantApps } from './tenantApps';

/**
 * Odoo-style app launcher. Shown when no startup app can be automatically resolved,
 * or when the user navigates to /select-app directly.
 */
export function SelectApp() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [, setActiveAppId] = useStore<string>(ACTIVE_APP_STORE_KEY, DEFAULT_ACTIVE_APP_ID);
    const { getInstalledApps, persistLastUsedApp, isLoaded } = useTenantApps();
    const identity = getIdentityCached();

    // Wait for live data before rendering — avoids flash of "No apps" while API call is in flight.
    if (!isLoaded) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    // No token after load = session expired → auto-redirect instead of stuck "No apps" screen.
    if (!getAccessToken()) {
        return <Navigate replace to="/login" />;
    }

    if (identity?.companyLicenseLocked) {
        return (
            <Box sx={{ p: 3, maxWidth: 560, mx: 'auto', mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    License expired
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your software license has expired. Use the links below to view renewal
                    information or sign out.
                </Typography>
                <Button component={Link} to="/license-renewal" variant="contained" sx={{ mr: 1 }}>
                    Renewal information
                </Button>
                <Button component={Link} to="/self-profile" variant="outlined" sx={{ mr: 1 }}>
                    Profile
                </Button>
                <Button component={Link} to="/license-blocked" variant="text">
                    Details
                </Button>
            </Box>
        );
    }

    const apps = getInstalledApps();

    const openApp = (appId: string, basePath: string) => {
        setActiveAppId(appId);
        persistLastUsedApp(appId);
        sessionStorage.setItem('sessionActiveAppId', appId);
        navigate(workspaceRootPath(appId) || basePath, { replace: true });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: { xs: 6, sm: 10 },
                px: 3,
                bgcolor: 'background.default',
            }}
        >
            <Typography variant="h4" fontWeight={700} gutterBottom align="center">
                {translate('shell.select_app.title', { _: 'Welcome' })}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 5 }}>
                {translate('shell.select_app.subtitle', {
                    _: 'Choose an application to get started.',
                })}
            </Typography>

            {apps.length === 0 ? (
                <Typography color="error" align="center">
                    {translate('shell.select_app.none', {
                        _: 'No applications are assigned to your account. Contact your administrator.',
                    })}
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(4, 1fr)',
                        },
                        gap: 2,
                        maxWidth: 900,
                        width: '100%',
                    }}
                >
                    {apps.map(app => (
                        <Paper
                            key={app.id}
                            elevation={1}
                            onClick={() => openApp(app.id, app.basePath)}
                            sx={{
                                p: 2.5,
                                cursor: 'pointer',
                                borderTop: 4,
                                borderColor: app.accentColor ?? 'primary.main',
                                userSelect: 'none',
                                transition: 'box-shadow 0.15s',
                                '&:hover': {
                                    boxShadow: 4,
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {app.name}
                            </Typography>
                            {app.description ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5, lineHeight: 1.4 }}
                                >
                                    {app.description}
                                </Typography>
                            ) : null}
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default SelectApp;
